import type { Countries } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import {
  maxLength,
  minLength,
  noWhiteStartOrEnd,
  required,
  validateIBAN
} from '@ps/web-shared-validators';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import countries from '@swf/generated/countries.json';

import CountrySelect from '../../../../CountrySelect/CountrySelect';
import TextInput from '../../../../TextInput/TextInput';
import RMFDropDown from '../../RMFDropDown/RMFDropDown';

import {
  TXTCLI_Please_enter_BICX,
  TXTWCL_Bank_country,
  TXTWCL_Bank_name_1,
  TXTWCL_IBAN_number,
  TXTWCL_Please_enter_Bank_full_nameX,
  TXTWCL_Please_enter_IBANX,
  TXTWCL_Please_enter_a_valid_IBANX,
  TXTWCL_Please_enter_a_valid_SWIFT_BIC_ELL,
  TXTWCL_SWIFT_BIC,
  TXTWCL_Select_bank_country
} from './BankFields.messages.json';

enum BankFieldNames {
  BANK_COUNTRY_FIELD = 'BANKCOUNTRY',
  BANK_NAME_FIELD = 'BANKNAME',
  IBAN_FIELD = 'IBAN',
  BIC_FIELD = 'BIC'
}

const BANK_NAME_MAX_LENGTH = 100;
const IBAN_MAX_LENGTH = 50;
const BIC_MIN_LENGTH = 8;
const BIC_MAX_LENGTH = 11;

const parseBankName = (value: string) => value.replace(/[^ A-Za-z0-9-]/g, '');
const parseInputUpperCase = (value: string) => value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

const BankFields: React.FC = () => {
  const country = useSelector(select.account.getCountry);
  const defaultOption = (countries as Countries).find(({ value }) => value === country);
  const {
    data: { dbtFieldsData: { fields } = {} }
  } = useWizardContext();

  const bankNameData = fields?.find(({ id }) => id === BankFieldNames.BANK_NAME_FIELD);
  const initialIban = fields?.find(({ id }) => id === BankFieldNames.IBAN_FIELD);
  const initialBic = fields?.find(({ id }) => id === BankFieldNames.BIC_FIELD);

  const {
    input: { value: bankCountry }
  } = useField(BankFieldNames.BANK_COUNTRY_FIELD, {
    subscription: { value: true },
    initialValue: country
  });

  const {
    input: { value: bankName }
  } = useField(BankFieldNames.BANK_NAME_FIELD, {
    subscription: { value: true },
    initialValue: bankNameData?.value
  });

  const {
    input: { value: iban }
  } = useField(BankFieldNames.IBAN_FIELD, {
    subscription: { value: true },
    initialValue: initialIban?.value
  });

  const {
    input: { value: bic }
  } = useField(BankFieldNames.BIC_FIELD, {
    subscription: { value: true },
    initialValue: initialBic?.value
  });

  return (
    <fieldset>
      <TextInput
        id={BankFieldNames.BANK_NAME_FIELD}
        value={bankName}
        label={TXTWCL_Bank_name_1}
        maxLength={BANK_NAME_MAX_LENGTH}
        parse={parseBankName}
        readOnly={bankNameData?.readOnly}
        validators={[required(TXTWCL_Please_enter_Bank_full_nameX), noWhiteStartOrEnd]}
      />
      <CountrySelect name={BankFieldNames.BANK_COUNTRY_FIELD} title={TXTWCL_Bank_country}>
        <RMFDropDown
          id={BankFieldNames.BANK_COUNTRY_FIELD}
          options={countries}
          label={TXTWCL_Bank_country}
          placeholder={<Translation {...TXTWCL_Select_bank_country} />}
          validators={[required()]}
          defaultOption={defaultOption}
          disabled
        />
      </CountrySelect>
      <TextInput
        id={BankFieldNames.IBAN_FIELD}
        value={iban}
        label={TXTWCL_IBAN_number}
        maxLength={IBAN_MAX_LENGTH}
        parse={parseInputUpperCase}
        readOnly={initialIban?.readOnly}
        validators={[
          required(TXTWCL_Please_enter_IBANX),
          validateIBAN(bankCountry, TXTWCL_Please_enter_a_valid_IBANX)
        ]}
      />
      <TextInput
        id={BankFieldNames.BIC_FIELD}
        value={bic}
        readOnly={initialBic?.readOnly}
        label={TXTWCL_SWIFT_BIC}
        parse={parseInputUpperCase}
        validators={[
          required(TXTCLI_Please_enter_BICX),
          minLength(BIC_MIN_LENGTH, TXTWCL_Please_enter_a_valid_SWIFT_BIC_ELL),
          maxLength(BIC_MAX_LENGTH, TXTWCL_Please_enter_a_valid_SWIFT_BIC_ELL)
        ]}
      />
    </fieldset>
  );
};

export default BankFields;
